<script lang="ts">
  import type { App } from '$lib/types';
  import { createEventDispatcher, getContext } from 'svelte';
  import { captureException } from '@sentry/sveltekit';
  import { gql, mutate } from '$lib/apollo';
  import { Toasts, session } from '$lib/stores';
  import { BASE_APP_URL, IRK_API_URL, STATIC_URL } from '$lib/constants';
  import { afterLogout, fetch } from '$lib/utils';

  import Button from '$components/mds/Buttons/Button/Button.svelte';
  import FormControl from '$components/FormControl.svelte';
  import SuitUpAnimation from './illustrations/SuitUpAnimation.svelte';

  import { faArrowLeft } from '@fortawesome/pro-solid-svg-icons';

  const { refreshSession } = getContext<App>('app');
  const dispatch = createEventDispatcher();

  export let name = '';
  export let variant: 'full' | 'contained' = 'full';
  let className = '';
  export { className as class };

  let loading = false;
  let elUsername: HTMLInputElement;

  async function chooseUsername() {
    if (name === '') {
      Toasts.push({
        kind: 'error',
        content: 'Please enter a username'
      });
      elUsername.focus();
      return;
    }
    try {
      loading = true;
      const resp = await mutate({
        mutation: gql`
          mutation EditAccountName($input: AccountEditInput!) {
            accountEdit(input: $input) {
              result {
                id
                name
                slug
              }
              errors {
                ... on Error {
                  message
                }
              }
            }
          }
        `,
        variables: {
          input: {
            account: {
              name,
              slug: name
            }
          }
        }
      });
      if (!resp.data.accountEdit?.result) {
        Toasts.push({
          kind: 'error',
          content: 'Something went wrong. Please try again later'
        });
        return;
      }
      // Update the account information after the refetch
      const { accessToken, refreshToken } = session.get();
      await refreshSession(accessToken!, refreshToken!);
      dispatch('done');
    } catch (e) {
      captureException(e);
      Toasts.push({
        kind: 'error',
        content: 'Something went wrong. Please try again later'
      });
    } finally {
      loading = false;
    }
  }

  async function logout() {
    try {
      await fetch(`${BASE_APP_URL}/auth/proxy/logout.json`, { method: 'post' });
    } catch (error) {
      captureException(error);
    } finally {
      // Hard reload to drop all cache and start fresh on the home page.
      afterLogout();
      window.location.assign(BASE_APP_URL + '/auth/account/create');
    }
  }
</script>

<div class="suit-up flex h-full w-full flex-col px-6 xl:my-auto xl:px-0">
  {#if variant === 'full'}
    <Button
      on:click={logout}
      variant="secondary"
      width="hug"
      size="md"
      class="self-start !pl-4 !pr-5 xl:hidden"
      icon={faArrowLeft}
    >
      Back
    </Button>
  {/if}

  <div class="{className} mx-auto mt-6 flex h-full flex-col xl:mt-0 xl:flex-row">
    {#if variant === 'full'}
      {#if name !== ''}
        <!-- hide temporarily while we figure out the text-run code and file size -->
        <!-- <div
          class="z-1 absolute left-[200px] top-[64px] flex min-w-0 max-w-[200px] origin-center -translate-x-1/2 rotate-[-2.87deg] truncate rounded-[4px] border-4 border-[#B37CF6] bg-[#8667DB] px-2 py-1 sm:left-[200px] xl:left-[242px] xl:top-[179px]"
          in:fly={{ y: 8, opacity: 0, duration: 150 }}
          out:fade={{ duration: 150 }}
        >
          <span class="font-display m-auto text-sm font-bold leading-none text-[#E6E1EA]">{name}</span>
        </div> -->
      {/if}
      <div
        class="illustration-bg relative xl:w-1/2"
        style="--illustration-url: url('{STATIC_URL}/illustrations/suitup_bg.svg');"
      >
        <SuitUpAnimation
          username={name}
          class="relative -left-[3%] m-auto w-[600px] max-w-64 xl:-left-[15%] xl:-top-[7%] xl:max-h-[125%] xl:max-w-none"
        />
      </div>
    {/if}
    <div class="mt-10 flex w-full flex-1 flex-col xl:mt-0 xl:justify-end xl:p-10 xl:pt-6">
      {#if variant === 'full'}
        <Button
          on:click={logout}
          variant="secondary"
          width="hug"
          size="md"
          class="mb-auto hidden self-start !pl-4 !pr-5 xl:flex"
          icon={faArrowLeft}
        >
          Back
        </Button>
      {/if}
      <h2 class="font-display md:text-3.25xl text-ondark-primary mb-3 text-center text-xl font-semibold xl:text-left">
        Suit up. What should we call you?
      </h2>
      <p class="text-body-base text-ondark-secondary mb-8 text-center xl:text-left">
        Add a username that fits you, this is how you’ll appear to others on Metafy. You can change your username later
        if you’d like.
      </p>

      <form on:submit={chooseUsername}>
        <FormControl
          bind:el={elUsername}
          type="text"
          name="username"
          placeholder="Username"
          value={name}
          maxlength={20}
          on:input={event => (name = event.target.value)}
          class="mb-8"
        />
        <Button
          type="submit"
          variant="primary"
          size="lg"
          width="full"
          disabled={name === ''}
          {loading}
          on:click={chooseUsername}>Suit up</Button
        >
      </form>
    </div>
  </div>
</div>

<style>
  :global(.auth-nav:has(+ .suit-up)) {
    display: none;
  }
  .illustration-bg {
    @screen xl {
      background-image: var(--illustration-url);
    }
  }
</style>
